// ─── Operator Types ──────────────────────────────────────────────────────────

export enum OperatorType {
  FLYING_SCHOOL = 'flying_school',
  AIR_TRANSPORT_AIRWORK = 'air_transport_airwork',
}

// ─── System Roles (non-deletable templates) ───────────────────────────────────

export enum SystemRole {
  PILOT = 'pilot',
  INSTRUCTOR = 'instructor',
  OPS_CONTROLLER = 'ops_controller',
  HAAMC = 'haamc',
  ADMIN = 'admin',
  STUDENT = 'student',
  CLIENT = 'client',
  // Escalation-recipient add-on roles (Enquiries module). Held IN ADDITION to a base role;
  // grant no module access beyond escalation recipiency. senior_base_pilot's display label is
  // company-configurable; ops_manager is the tier-2 (Operations Manager) recipient.
  SENIOR_BASE_PILOT = 'senior_base_pilot',
  OPS_MANAGER = 'ops_manager',
}

// ─── Permission Dimensions ────────────────────────────────────────────────────

export enum ModuleAccess {
  NONE = 'none',
  READ = 'read',
  WRITE = 'write',
  FULL = 'full',
}

export enum RecordScope {
  OWN = 'own',
  BASE = 'base',
  ALL = 'all',
}

// ─── Flight Types ─────────────────────────────────────────────────────────────

export enum FlightType {
  PVT = 'PVT',
  AWK = 'AWK',
  TRG = 'TRG',
  CHTR = 'CHTR',  // displays as "Air Transport / Airwork" in UI
  POS = 'POS',
}

export const FLIGHT_TYPE_LABELS: Record<FlightType, string> = {
  [FlightType.PVT]: 'Private',
  [FlightType.AWK]: 'Aerial Work',
  [FlightType.TRG]: 'Training',
  [FlightType.CHTR]: 'Air Transport / Airwork',
  [FlightType.POS]: 'Positioning',
};

// ─── Aircraft Enums ───────────────────────────────────────────────────────────

export enum AircraftCategory {
  FIXED_WING = 'fixed_wing',
  HELICOPTER = 'helicopter',
  GYROPLANE = 'gyroplane',
  AMPHIBIAN = 'amphibian',
  FLOATPLANE = 'floatplane',
  OTHER = 'other',
}

export enum FuelType {
  AVGAS = 'AVGAS',
  JETA1 = 'JetA1',
  MOGAS = 'MOGAS',
}

export enum FuelUnit {
  L = 'L',
  LBS = 'lbs',
  US_GAL = 'us_gal',
}

export enum TrackingMode {
  FULL = 'FULL',
  SHARED = 'SHARED',
  THIRD_PARTY = 'THIRD_PARTY',
  HISTORICAL = 'HISTORICAL',
}

// ─── Fuel Conversion Constants ────────────────────────────────────────────────

export const FUEL_SG = {
  [FuelType.AVGAS]: 0.72,
  [FuelType.JETA1]: 0.80,
  [FuelType.MOGAS]: 0.72,
} as const;

// Convert litres to kg: litres × SG
export function litresToKg(litres: number, fuelType: FuelType): number {
  return litres * FUEL_SG[fuelType];
}

// Convert litres to lbs: litres × SG × 2.2046
export function litresToLbs(litres: number, fuelType: FuelType): number {
  return litres * FUEL_SG[fuelType] * 2.2046;
}

// Convert lbs to litres (for JetA1 aircraft that track in lbs)
export function lbsToLitres(lbs: number, fuelType: FuelType): number {
  return lbs / (FUEL_SG[fuelType] * 2.2046);
}

// ─── Booking / Task Enums ─────────────────────────────────────────────────────

export enum BookingStatus {
  ENQUIRY = 'enquiry',
  QUOTED = 'quoted',
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
}

export enum NotifyStatus {
  DRAFT = 'draft',
  NOTIFIED = 'notified',
  CREW_CONFIRMED = 'crew_confirmed',
  MODIFIED = 'modified',
}

export enum ApprovalStatus {
  NOT_REQUIRED = 'not_required',
  PENDING_STAFF = 'pending_staff',
  PENDING_ASSET = 'pending_asset',
  PENDING_BOTH = 'pending_both',
  APPROVED = 'approved',
  DECLINED = 'declined',
}

export enum TaskType {
  PASSENGER = 'passenger',
  FREIGHT = 'freight',
  TRAINING = 'training',
  DG = 'dg',
  COMBINED = 'combined',
  MEDIVAC = 'medivac',
  FERRY = 'ferry',
  AIRWORK = 'airwork',
}

export enum BillingStatus {
  READY = 'ready',
  SENT_TO_ACCOUNTS = 'sent_to_accounts',
  INVOICED = 'invoiced',
  PAID = 'paid',
}

// ─── Trip Record Enums ────────────────────────────────────────────────────────

export enum TrStatus {
  OPEN = 'open',
  SUBMITTED = 'submitted',
  CLOSED = 'closed',
}

export enum ReceiptStatus {
  RECEIPT = 'receipt',
  BOWSER = 'bowser',
  NONE = 'none',
}

// ─── Defect Log Enums ─────────────────────────────────────────────────────────

export enum DefectSeverity {
  GROUNDING = 'grounding',
  NON_GROUNDING = 'non_grounding',
  MONITOR = 'monitor',
}

export enum DefectStatus {
  RAISED = 'raised',
  UNDER_REVIEW = 'under_review',
  IN_RECTIFICATION = 'in_rectification',
  AWAITING_SIGN_OFF = 'awaiting_sign_off',
  CLOSED = 'closed',
}

// ─── Airspace Event Enums ─────────────────────────────────────────────────────

export enum AirspaceSeverity {
  ADVISORY = 'advisory',
  COORDINATION_REQUIRED = 'coordination_required',
  RESTRICTED = 'restricted',
  HARD_BLOCK = 'hard_block',
}

// ─── JWT Payload ──────────────────────────────────────────────────────────────

export interface JwtPayload {
  sub: string;             // user UUID
  email: string;
  tenantId: string;
  tenantSlug: string;
  roles: string[];
  resourceGroupId: string | null;
  iat: number;
  exp: number;
}

export interface RefreshTokenPayload {
  sub: string;
  tokenFamily: string;   // for refresh token rotation tracking
  jti: string;           // unique per-token UUID — prevents hash collisions on same-second rotation
  iat: number;
  exp: number;
}

// ─── API Response Wrappers ────────────────────────────────────────────────────

export interface ApiError {
  message: string;
  code?: string;
  details?: unknown; // optional structured payload (e.g. Step 5c.3 §B booking collisions)
}

// Standard response envelope (introduced Step 4.5b).
// Replaces the legacy { data, error: string, meta } shape. `warnings` is always
// present (often empty) and carries non-blocking advisories — e.g. an unknown
// ICAO accepted on a Base save (advisory not blocking, §8.1).
export interface ApiResponse<T> {
  data: T | null;
  error: ApiError | null;
  warnings: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  error: null;
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

// ─── Sync Status (offline-first) ─────────────────────────────────────────────

export enum SyncStatus {
  PENDING = 'pending',
  SYNCING = 'syncing',
  SYNCED = 'synced',
  CONFLICT = 'conflict',
  ERROR = 'error',
}

// ─── User Preferences ────────────────────────────────────────────────────────

export enum TimeEntryPref {
  PICKER = 'picker',
  TEXT = 'text',
  KEYPAD = 'keypad',
}

export enum ThemePref {
  LIGHT = 'light',
  DARK = 'dark',
  AUTO = 'auto',
}

export enum DarkAccentPref {
  BLUE = 'blue',
  AMBER = 'amber',
  RED = 'red',
}

export enum FontSizePref {
  STANDARD = 'standard',
  LARGE = 'large',
}

// ─── Unit Enums (company settings) ───────────────────────────────────────────

export enum WeightUnit { KG = 'kg', LBS = 'lbs' }
export enum VolumeUnit { LITRES = 'litres', US_GAL = 'us_gal', IMP_GAL = 'imp_gal' }
export enum TempUnit { CELSIUS = 'celsius', FAHRENHEIT = 'fahrenheit' }
export enum DistanceUnit { NM = 'nm', KM = 'km', MILES = 'miles' }
export enum AltitudeUnit { FT = 'ft', M = 'm' }
export enum SpeedUnit { KTS = 'kts', KMH = 'kmh', MPH = 'mph' }

// ─── Global navigation — role→route access (global nav, home_route) ────────────
//
// SINGLE SOURCE OF TRUTH for who-can-go-where in the app's global menu + the
// user-settable home page. The frontend imports these at runtime (menu gating, the
// Profile home-page dropdown, the Home button + post-login landing). The backend
// CANNOT runtime-import this package (it resolves @avsuite/shared to type-only
// declarations — see HANDOFF flat-build note), so `apps/backend/src/lib/navRoutes.ts`
// keeps a small, clearly-labelled MIRROR used to validate a submitted home_route.
// **If you change ROUTE_ACCESS_ROLES or the defaults here, update that mirror too.**

// Concrete, navigable routes that the global nav role-gates. 'all' = any authenticated
// user. "Soon" features (Air Transport board, Maintenance, Trip records, Airports) are
// NOT here — they have no route yet, so they can never be a valid home_route.
export const ROUTE_ACCESS_ROLES: Record<string, readonly SystemRole[] | 'all'> = {
  '/profile': 'all',
  '/ops-board': [SystemRole.OPS_CONTROLLER, SystemRole.INSTRUCTOR, SystemRole.ADMIN, SystemRole.HAAMC],
  '/admin': [SystemRole.ADMIN],
  '/admin/orphaned': [SystemRole.ADMIN],
  // Enquiries — staff-only; hidden from students/clients. Includes the two escalation-recipient
  // add-on roles so a recipient can open the enquiry they were notified about even if it is their
  // only role (mirrors backend STAFF_ROLE_NAMES on /api/enquiries).
  '/enquiries': [SystemRole.PILOT, SystemRole.INSTRUCTOR, SystemRole.OPS_CONTROLLER, SystemRole.HAAMC, SystemRole.ADMIN, SystemRole.SENIOR_BASE_PILOT, SystemRole.OPS_MANAGER],
  // Office Tasks — staff-only interoffice task scheduler; hidden from students/clients. Same staff
  // allow-list as Enquiries (mirrors backend STAFF_ROLE_NAMES on /api/office-tasks).
  '/tasks': [SystemRole.PILOT, SystemRole.INSTRUCTOR, SystemRole.OPS_CONTROLLER, SystemRole.HAAMC, SystemRole.ADMIN, SystemRole.SENIOR_BASE_PILOT, SystemRole.OPS_MANAGER],
} as const;

// May `roles` access the nav route `path`? Unknown path → false.
export function canAccessRoute(path: string, roles: readonly string[]): boolean {
  const allowed = ROUTE_ACCESS_ROLES[path];
  if (!allowed) return false;
  if (allowed === 'all') return true;
  return roles.some((r) => (allowed as readonly string[]).includes(r));
}

// Role default home page, used until the user sets `home_route` (and as the fallback when a
// stored value is no longer accessible). Operational staff → Ops Board; pilot → Ops Board for
// now (Trip records lands here once it ships); everyone else (student/client) → Profile.
export function defaultHomeRoute(roles: readonly string[]): string {
  const has = (r: string) => roles.includes(r);
  if (has(SystemRole.ADMIN) || has(SystemRole.OPS_CONTROLLER) || has(SystemRole.INSTRUCTOR) || has(SystemRole.HAAMC)) {
    return '/ops-board';
  }
  if (has(SystemRole.PILOT)) return '/ops-board'; // until Trip records exists
  return '/profile';
}

// A submitted home_route is valid iff it is a known nav route the user may access.
// (null/unset is always valid — it means "use the role default".) Server reuses this.
export function isValidHomeRoute(path: string | null | undefined, roles: readonly string[]): boolean {
  if (path == null) return true;
  return canAccessRoute(path, roles);
}

// Resolve the route to land a user on: their stored home_route if still accessible,
// otherwise the role default. Used by the Home button and post-login landing.
export function resolveHomeRoute(homeRoute: string | null | undefined, roles: readonly string[]): string {
  if (homeRoute && canAccessRoute(homeRoute, roles)) return homeRoute;
  return defaultHomeRoute(roles);
}

// Display label for a person/client in pickers, lists and booking detail (Phase 3; used more in
// Phases 4–5). Renders "Person · Company" when both a person name and a DISTINCT company are
// present; otherwise just the full_name (covers company-only records, where full_name already
// equals the company, and pre-Phase-3 rows with no company). Distinctly named to avoid colliding
// with BookingForm's existing local `personLabel(id)`.
export function personDisplayLabel(p: { full_name: string; company?: string | null }): string {
  const company = (p.company ?? '').trim();
  if (!company || p.full_name.trim() === company) return p.full_name;
  return `${p.full_name} · ${company}`;
}
